import React, {useCallback} from 'react';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import {EEventSource} from 'coreClasses/events/IEvents';
import {useCustomerMgr, useProduct} from 'coreHooks';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import Button from 'brandComponents/Button';
import Icon from 'brandComponents/Icon';
import {useWishlist} from 'brandHooks';

import {IAddToWishlistButtonProps} from './IAddToWishlistButton';
import messages from './messages';

import styles from './AddToWishlistButton.module.scss';

const AddToWishlistButton = ({
	classNames,
	productId,
	variationGroupId,
	source,
	sourceSpecification,
	quantity = 1,
	iconFill = 'wishlist-true',
	iconOutline = 'wishlist-false',
	aggregateVariationGroup = true
}: IAddToWishlistButtonProps) => {
	const {managerRepository} = useGeneralProvider();
	const product = useProduct(productId);
	const wishlist = useWishlist();
	const intl = useIntl();
	const customerMgr = useCustomerMgr();
	const customer = customerMgr.getCurrentCustomer();
	const hasExcludedSource = source !== EEventSource.PDP;
	let isProductInWishlist;

	variationGroupId = variationGroupId || product.getVariationGroupId();

	if ((productId === variationGroupId || product.isVariationGroup()) && hasExcludedSource) {
		if (product.hasMultipleVariationGroups() && aggregateVariationGroup) {
			isProductInWishlist = product
				.getVariationGroupIds()
				.reduce((currentValue, id) => [...wishlist.findItemsByVariationGroupId(id), ...currentValue], [])
				.length > 0;
		} else {
			isProductInWishlist = wishlist.findItemsByVariationGroupId(variationGroupId).length > 0;
		}
	} else {
		isProductInWishlist = !!wishlist.findItemByProductId(productId);
	}

	const title: string = intl.formatMessage(isProductInWishlist ? messages.addedToLabel : messages.addToLabel);
	const isWishlistEnabledNotAuth = managerRepository
		.getConfigurationsMgr()
		.get('sitePreferences.enableNotAuthenticatedCustomerWishlist');
	const handleClick = useCallback(() => {
		const productIdToStore: string = product.isVariant() ? productId : null;
		managerRepository.getUiMgr().setProductToAddToWishlist({
			productId: productIdToStore,
			variationGroupId,
			quantity,
			source,
			sourceSpecification,
			shouldWaitToReset: !aggregateVariationGroup,
		});
	}, [productId, variationGroupId]);

	return (
		<Button
			classNames={cn('add-to-wishlist-button', styles['add-to-wishlist-button'], classNames, isProductInWishlist && 'added')}
			onClick={handleClick}
			title={title}
		>
			<Icon
				name={!!isProductInWishlist && (isWishlistEnabledNotAuth || customer?.isRegistered)
					? iconFill : iconOutline
				}
				height={13}
			/>
		</Button>
	);
};

export default AddToWishlistButton;
